import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const stats = [
  { num: '800M+', label: 'Weekly active users on ChatGPT (2026)' },
  { num: '60%', label: 'Of Google searches now include an AI Overview' },
  { num: '47%', label: 'Of brands have zero GEO strategy' },
  { num: '10x', label: 'Growth in AI-driven web referral traffic since mid-2024' },
  { num: '<20%', label: 'Overlap between Google top results and AI-cited sources' },
];

export default function LocalImpactSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="local-impact" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[700px] h-[400px] bg-ds-red/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">The Local Business Impact</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              YOUR COMPETITORS
              <br />
              DON&apos;T KNOW
              <br />
              <span className="text-ds-red neon-text-red">THIS EXISTS YET.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-sm reveal">
              For national brands, GEO is becoming a priority. For local businesses in Australia, it&apos;s almost entirely uncharted territory — which means right now is the moment to move.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Main two-col layout */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Story */}
          <div className="reveal">
            <div className="border-l-2 border-ds-red pl-6 mb-8">
              <p className="font-body text-white/70 text-sm leading-relaxed mb-4">
                Think about the customer journey for a local tourism or experiences business. Someone planning a holiday to Mission Beach wants to learn to dive. They open ChatGPT and ask:
              </p>
              <p className="font-display text-lg text-white italic leading-snug">
                &ldquo;What is the best PADI dive school near Mission Beach?&rdquo;
              </p>
            </div>
            <p className="font-body text-white/60 text-sm leading-relaxed mb-4">
              That AI has been trained on thousands of websites, reviews, directories, and mentions from across the internet. If your business has built the right signals — consistent reviews, structured content, authoritative mentions, clear expertise — you show up.
            </p>
            <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
              If you haven&apos;t, you don&apos;t.
            </p>
            <div className="border border-ds-red/30 bg-ds-red/5 p-5">
              <p className="font-display text-base text-white leading-tight">
                The business that gets recommended in that answer{' '}
                <span className="text-ds-red">wins the booking before the customer even visits a website.</span>
              </p>
              <p className="font-body text-white/50 text-xs mt-3">
                No Google Ad spend. No SEO competition. Just a direct recommendation from the AI that customer already trusts.
              </p>
            </div>
          </div>

          {/* Stats column */}
          <div className="space-y-4 reveal">
            <p className="font-display text-ds-red text-xs tracking-widest uppercase mb-6">The Numbers Behind the Shift</p>
            {stats.map((s) => (
              <div key={s.num} className="flex items-center gap-5 border border-ds-border bg-ds-bg p-5 group hover:border-ds-red/30 transition-colors duration-300">
                <div className="font-display text-3xl text-ds-red leading-none flex-shrink-0 w-24 text-right">
                  {s.num}
                </div>
                <div className="h-8 w-px bg-ds-border flex-shrink-0" />
                <p className="font-body text-white/60 text-sm leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="border border-ds-border bg-ds-bg p-10 text-center reveal">
          <p className="font-display text-2xl md:text-3xl text-white leading-tight max-w-3xl mx-auto">
            The brands building GEO discipline now will earn{' '}
            <span className="text-ds-red">compounding advantages</span> as AI becomes the primary way customers discover who to trust.
          </p>
          <div className="w-12 h-px bg-ds-red mx-auto mt-6" />
        </div>

      </div>
    </section>
  );
}
